import { api } from "@/lib/api";
import type { Journey, StudioContentStatus } from "../schema";

export const CONTENT_JOURNEY = "/content-journeys";

/** Studio builder search (e.g. add content to journey days). */
export const STUDIO_CONTENT_SEARCH = "/api/studio/content-search";

export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds == null) return "";
  const s = Math.trunc(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.trunc(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

export interface StudioContentSearchResult {
  id: string;
  title: string;
  contentType: "audio" | "video" | "workbook";
  status: string;
  description: string | null;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  language: string | null;
  categoryId?: string | null;
  tagOptionIds?: string[];
}

export const ContentJourneyService = {
  async searchStudioContent(
    searchParamsStr: string,
  ): Promise<StudioContentSearchResult[]> {
    return api.get<StudioContentSearchResult[]>(
      `${STUDIO_CONTENT_SEARCH}?${searchParamsStr}`,
    );
  },
  async findAll(filters?: {
    search?: string;
    status?: StudioContentStatus;
    categoryId?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    items: Array<{
      id: string;
      title: string;
      description: string | null;
      totalDays: number | null;
      estimatedDailyMinutes: number | null;
      difficultyLevel: string | null;
      progressionType: string | null;
      status: StudioContentStatus;
      version: number;
      updatedAt: string;
      createdAt: string;
      createdByName: string | null;
      ownerId: string | null;
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.page) params.set("page", filters.page.toString());
    if (filters?.limit) params.set("limit", filters.limit.toString());

    return api.get(`${CONTENT_JOURNEY}?${params.toString()}`);
  },

  async findOne(id: string): Promise<Journey> {
    return api.get<Journey>(`${CONTENT_JOURNEY}/${id}`);
  },

  async create(payload: Record<string, unknown>): Promise<Journey> {
    return api.post<Journey>(CONTENT_JOURNEY, payload);
  },

  async update(id: string, payload: Record<string, unknown>): Promise<Journey> {
    return api.patch<Journey>(`${CONTENT_JOURNEY}/${id}`, payload);
  },

  async submitForReview(id: string): Promise<Journey> {
    return api.post<Journey>(`${CONTENT_JOURNEY}/${id}/submit`, {});
  },

  async createVersion(id: string): Promise<{
    id: string;
    version: number;
    versionId?: string;
  }> {
    return api.post(`${CONTENT_JOURNEY}/${id}/create-version`, {});
  },

  async archive(id: string): Promise<Journey> {
    return api.post<Journey>(`${CONTENT_JOURNEY}/${id}/archive`, {});
  },

  async restore(id: string): Promise<Journey> {
    return api.post<Journey>(`${CONTENT_JOURNEY}/${id}/restore`, {});
  },
};
